import type { PropertyOnCard } from '@@/shared/property/types';
import { type PropertySearchCriteria, propertyTypeToSearchPropertyType } from '@@/shared/property/search-criteria';

export interface FilterParams {
  properties: PropertyOnCard[]
  propertySearchCriteria: PropertySearchCriteria
}

function byTransactionType(filterParams: FilterParams) {
  const { properties, propertySearchCriteria } = filterParams;
  const { transactionType } = propertySearchCriteria;
  if (!transactionType) return filterParams;
  return {
    propertySearchCriteria,
    properties: properties.filter(property => property.transactionType === transactionType),
  };
}

function byPropertyTypes(filterParams: FilterParams): FilterParams {
  const { properties, propertySearchCriteria } = filterParams;
  const { propertyTypes } = propertySearchCriteria;
  if (!propertyTypes || propertyTypes.length === 0) return filterParams;

  return {
    propertySearchCriteria,
    properties: properties.filter((property) => {
      const searchPropertyType = propertyTypeToSearchPropertyType(property.propertyType);
      return propertyTypes.includes(searchPropertyType);
    }),
  };
}

function byRooms(filterParams: FilterParams) {
  const { properties, propertySearchCriteria } = filterParams;
  const { rooms } = propertySearchCriteria;
  if (!rooms || rooms.length === 0) return filterParams;
  const filteredPropertiesByRooms = properties.filter((property) => {
    const propertyRoomsCount = property.rooms.find(room => room.type === 'rooms')?.value ?? 0;
    return rooms.includes(propertyRoomsCount);
  });
  return {
    propertySearchCriteria,
    properties: filteredPropertiesByRooms,
  };
}

export function filterProperties(filterParams: FilterParams) {
  return [byTransactionType, byPropertyTypes, byRooms].reduce((acc, fn) => fn(acc), filterParams);
}

export const useAgentPropertyFilters = (filterParams: Ref<FilterParams>) => {
  const filteredProperties = computed(() => {
    const { properties: filteredProperties } = filterProperties(filterParams.value);
    const { sort } = filterParams.value.propertySearchCriteria;
    if (sort === 'priceAsc') return filteredProperties.slice().sort((a, b) => a.prices.main - b.prices.main);
    else if (sort === 'priceDesc') return filteredProperties.slice().sort((a, b) => b.prices.main - a.prices.main);
    return filteredProperties;
  });

  const authorizedCriterias: Array<keyof PropertySearchCriteria> = ['transactionType', 'propertyTypes', 'rooms'];
  const filtersCount = computed(() => {
    return Object.entries(filterParams.value.propertySearchCriteria)
      .filter(([key]) => authorizedCriterias.includes(key as keyof PropertySearchCriteria))
      .reduce((acc, [_, value]) => {
        if (!value) return acc;
        return acc + (Array.isArray(value) ? value.length : 1);
      }, 0);
  });

  return {
    filtersCount,
    filteredProperties,
  };
};
